import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Injector,
  ViewChild,
  computed,
  inject,
} from "@angular/core";
import { DatePipe } from "@angular/common";

import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";

import { TIME_DIRECTION_AGO } from "@bitwarden/web-vault/app/components/date-picker/calendar/calendar.component";
import { focusMovedOutside } from "@bitwarden/angular/utils/focus-moved-outside";
import {
  calculatePickerTranslateValue,
  escapeBelongsToInnerDropdown,
  refocusPickerTrigger,
} from "../datepicker-utils";

/**
 * Today view single-date picker. User picks ONE date in the range
 * [first transaction date .. today]; default is today. Selecting a date sets the
 * today-view period end to that date (start stays at the first transaction).
 * UI mirrors the past/future view datepickers.
 */
@Component({
  selector: "app-today-views-datepicker",
  templateUrl: "./today-views-datepicker.component.html",
})
export class TodayViewsDatepickerComponent implements AfterViewChecked {
  private todayStore = inject(CalculationTodayStoreService);
  private datePipe = inject(DatePipe);
  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);

  @ViewChild("pickerTrigger", { read: ElementRef }) pickerTrigger!: ElementRef;
  @ViewChild("picker", { read: ElementRef }) picker!: ElementRef;
  pickerTranslateValue: string = "0";

  showPicker = false;
  protected readonly AGO = TIME_DIRECTION_AGO;

  get isPickerOpen(): boolean {
    return this.showPicker;
  }

  // Selected single date = the current period end (defaults to today).
  selectedDate = computed(() => this.todayStore.period.selected().endDate);

  displayDate = computed(() => this.datePipe.transform(this.selectedDate(), "yyyy-MM-dd"));

  // Selectable range: any date up to today (no lower bound), mirroring the From calendar.
  dateFilter = computed(() => {
    const max = BalanceDatesUtils.getEndOfDayDate(new Date());
    return (date: Date) => date <= max;
  });

  ngAfterViewChecked() {
    this.updatePickerTranslateValue();
    this.cdr.detectChanges();
  }

  @HostListener("window:resize")
  onResize() {
    this.updatePickerTranslateValue();
  }

  togglePicker() {
    this.showPicker = !this.showPicker;
  }

  onDateSelected(date: Date) {
    this.todayStore.period.updateSelection({
      startDate: this.todayStore.period.options().startDate,
      endDate: date,
      _isDefault: false,
    });
    this.closePicker();
  }

  // On the document, like the other popups' appClickOutside: focus may not be in the picker
  // (#6245).
  @HostListener("document:keydown.escape", ["$event"])
  onEscape(event?: KeyboardEvent) {
    if (
      this.showPicker &&
      !event?.defaultPrevented &&
      !escapeBelongsToInnerDropdown(this.elementRef.nativeElement, event)
    ) {
      this.closePicker();
    }
  }

  /** Closes from inside the picker (Confirm, Cancel, Escape), handing focus back to the trigger. */
  protected closePicker() {
    refocusPickerTrigger(this.pickerTrigger?.nativeElement, this.injector);
    this.showPicker = false;
  }

  updatePickerTranslateValue() {
    this.pickerTranslateValue = calculatePickerTranslateValue(this.pickerTrigger, this.picker);
  }

  @HostListener("document:mouseup", ["$event"])
  onClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showPicker = false;
    }
  }

  // Keyboard counterpart of the outside click above: Tab past the open picker's last control (or
  // Shift+Tab before its trigger) closes it the same way.
  @HostListener("focusout", ["$event"])
  onFocusOut(event: FocusEvent) {
    if (this.showPicker && focusMovedOutside(event, this.elementRef.nativeElement)) {
      this.showPicker = false;
    }
  }
}
